import {
    afterNextRender,
    DestroyRef,
    Directive,
    effect,
    ElementRef,
    inject,
    input,
} from '@angular/core';

function focusElement(node: HTMLElement): void {
    const tagName = node.tagName.toLowerCase();
    const isNaturallyFocusable =
        tagName === 'input' ||
        tagName === 'button' ||
        tagName === 'select' ||
        tagName === 'textarea' ||
        tagName === 'a';

    if (node.getAttribute('tabindex') === null && !isNaturallyFocusable) {
        node.setAttribute('tabindex', '-1');
    }

    if (!isNaturallyFocusable || node.getAttribute('tabindex') === '-1') {
        node.style.outline = 'none';
    }

    setTimeout(() => node.focus(), 0);
}

// Usage: <input [appAutofocus]="true"> or <div [appAutofocus]="index">

@Directive({
    selector: '[appAutofocus]',
    standalone: true,
})
export class AutofocusDirective {
    readonly enabled = input<boolean | string | number | unknown>(true, {alias: 'appAutofocus'});

    private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);

    constructor() {
        effect(() => {
            const value = this.enabled();
            if (value !== false && value !== 'false' && value !== null && value !== undefined) {
                focusElement(this.el.nativeElement);
            }
        });

        afterNextRender(() => {
            const value = this.enabled();
            if (value !== false && value !== 'false' && value !== null && value !== undefined) {
                focusElement(this.el.nativeElement);
            }
        });
    }
}

/**
 * Prevents NgbModal/NgbOffcanvas from auto-focusing .btn-close buttons on open.
 * Redirects focus to .modal-body (modals) or the panel's first child container (offcanvas).
 *
 * Add to root component imports to enable globally:
 *   imports: [NgbAutoFocusFixDirective]
 */
@Directive({
    selector: '[appNgbAutoFocusFix]',
    standalone: true,
})
export class NgbAutoFocusFixDirective {
    constructor() {
        const destroyRef = inject(DestroyRef);

        afterNextRender(() => {
            const onFocusIn = (event: Event): void => {
                const target = event.target;
                if (!(target instanceof HTMLElement) || !target.classList.contains('btn-close')) {
                    return;
                }

                const container =
                    target.closest('ngb-modal-window') ?? target.closest('ngb-offcanvas-panel');
                if (!container) {
                    return;
                }

                const focusTarget =
                    container.querySelector<HTMLElement>('.modal-body') ??
                    container.querySelector<HTMLElement>('.offcanvas-body') ??
                    container.firstElementChild;
                if (focusTarget instanceof HTMLElement) {
                    const tabindex = focusTarget.getAttribute('tabindex');
                    if (tabindex === null || tabindex === '') {
                        focusTarget.setAttribute('tabindex', '-1');
                    }
                    focusTarget.style.outline = 'none';
                    focusTarget.focus();
                }
            };

            document.addEventListener('focusin', onFocusIn);
            destroyRef.onDestroy(() => document.removeEventListener('focusin', onFocusIn));
        });
    }
}
